/**
 * Bosh ekran (TZ §10.4).
 *
 * Toifa yonidagi RAQAM majburiy — bo'sh toifaga bosgan odam qaytmaydi.
 * 8 ta toifa birdan ko'rsatilmaydi: 4 tasi asosiy, qolgani ochiladi.
 */

import { t } from "../i18n";
import { useEffect, useState } from "react";
import { api, getCached } from "../api";
import { CardList, CardListSkeleton, Chip, EmptyState, ErrorView, Section } from "../components";
import {
  IconBolt,
  IconChevronDown,
  IconClock,
  IconGrid,
  IconSearch,
  IconSparkle,
  IconSprout,
  TypeIcon,
} from "../icons";
import { quickLabel, typeLabel } from "../format";
import type { Holiday } from "../holidays";
import { Ribbon } from "../ribbon";
import { navigate } from "../router";
import type { HomeResponse, QuickFilterKey } from "../types";

const QUICK_ROUTE: Record<QuickFilterKey, Record<string, string | boolean | number>> = {
  no_ielts: { no_ielts: true },
  full_funding: { funding: "full" },
  deadline_soon: { deadline_within: 14 },
};

export default function Home({ holiday }: { holiday: Holiday | null }) {
  const [data, setData] = useState<HomeResponse | null>(() => getCached<HomeResponse>("home"));
  const [error, setError] = useState<string | null>(null);
  const [allCategories, setAllCategories] = useState(false);

  async function load() {
    setError(null);
    try {
      const fresh = await api.home();
      setData(fresh);
    } catch (err) {
      if (!data) {
        setError(err instanceof Error ? err.message : t("common.loadFailed"));
      }
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (error && !data) return <ErrorView message={error} onRetry={load} />;
  if (!data) {
    return (
      <div className="screen">
        <button
          className="search-bar"
          type="button"
          onClick={() => navigate({ name: "search", query: {} })}
        >
          <IconSearch size={19} />
          {t("home.search")}
        </button>
        <Section title={t("home.forYou")} icon={<IconSparkle size={17} />}>
          <CardListSkeleton count={4} />
        </Section>
      </div>
    );
  }

  const categories = allCategories ? data.categories : data.categories.slice(0, 4);

  return (
    <div className="screen">
      <Ribbon
        holiday={holiday}
        banners={data.banners}
        onEvent={(kind, id) =>
          void api.track({ kind, meta: { banner_id: id } }).catch(() => {})
        }
      />

      <button
        className="search-bar"
        type="button"
        onClick={() => navigate({ name: "search", query: {} })}
      >
        <IconSearch size={19} />
        {t("home.search")}
      </button>

      <Section title={t("home.quickFilters")} icon={<IconBolt size={17} />}>
        <div className="chip-row">
          {data.quick_filters.map((filter) => (
            <Chip
              key={filter.key}
              count={filter.count}
              onClick={() =>
                navigate({ name: "search", query: QUICK_ROUTE[filter.key] })
              }
            >
              {quickLabel(filter.key, filter.label)}
            </Chip>
          ))}
        </div>
      </Section>

      <Section
        title={t("home.forYou")}
        icon={<IconSparkle size={17} />}
        action={
          !data.onboarded ? (
            <button
              className="link-btn"
              type="button"
              onClick={() => navigate({ name: "onboarding" })}
            >
              {t("home.settings")}
            </button>
          ) : null
        }
      >
        {data.for_you.length ? (
          <CardList items={data.for_you} source="feed" />
        ) : (
          <EmptyState
            icon={<IconSprout size={26} />}
            title={t("home.empty")}
            hint={t("home.emptyHint")}
          />
        )}
      </Section>

      <Section title={t("home.categories")} icon={<IconGrid size={17} />}>
        <div className="category-grid">
          {categories.map((category) => (
            <button
              key={category.type}
              className="category"
              type="button"
              disabled={category.count === 0}
              onClick={() =>
                navigate({ name: "search", query: { type: category.type } })
              }
            >
              <span className="category-icon">
                <TypeIcon type={category.type} size={19} />
              </span>
              <span className="category-label">
                {typeLabel(category.type, category.label)}
              </span>
              <b className="category-count">{category.count}</b>
            </button>
          ))}
        </div>
        {data.categories.length > 4 ? (
          <button
            className="link-btn"
            type="button"
            onClick={() => setAllCategories(!allCategories)}
          >
            {allCategories ? t("home.hide") : t("home.allCategories")}
            <IconChevronDown
              size={15}
              style={{
                transform: allCategories ? "rotate(180deg)" : undefined,
                transition: "transform var(--dur) var(--ease)",
              }}
            />
          </button>
        ) : null}
      </Section>

      {data.deadline_soon.length ? (
        <Section
          title={t("home.deadlineSoon")}
          icon={<IconClock size={17} />}
          action={
            <button
              className="link-btn"
              type="button"
              onClick={() =>
                navigate({ name: "search", query: { sort: "deadline" } })
              }
            >
              {t("home.all")}
            </button>
          }
        >
          <CardList items={data.deadline_soon} source="feed" />
        </Section>
      ) : null}
    </div>
  );
}
